import { useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { SYSTEM_VARIABLES, systemVariable, type ContractRole, type CustomVariableDef } from "@talentkontor/domain";
import { BLOCK_LABEL, auszeichnungUmschalten, blockDepths, blockId, blockProblems, blocksFromText, insertionIndex, type Block, type BlockAlign, type BlockKind } from "./bloecke";
import { ROLE_LABEL } from "./modell";
import "./vertraege.css";
import { nameIn, t } from "../../../lib/sprache";

const NS_LABEL: Record<string, string> = { get agency() { return t("Agentur", "Agency"); }, talent: "Creator", get brand() { return t("Marke", "Brand"); }, get deal() { return t("Kooperation", "Deal"); }, get contract() { return t("Vertrag", "Contract"); }, get dates() { return t("Datum", "Date"); },
  // K-559 b: Angaben des ursprünglichen Vertrags (nur bei Nachträgen gefüllt)
  get original() { return t("Ursprungsvertrag (bei Nachträgen)", "Original contract (for addenda)"); } };
const ADDABLE: BlockKind[] = ["h2", "p", "ol", "ul", "h3", "signature", "if", "pagebreak", "note"];
const FILTER_HINT: Record<string, string> = { money: "|money", date: "|date", percent: "|percent", lines: "" };
/** Abschnitte mit Text (Werkzeugleiste und Tastenkürzel) */
const TEXT_KINDS: BlockKind[] = ["h1", "h2", "h3", "p", "note", "ol", "ul", "html"];

/** Platzhalter mit passendem Format je Typ, z. B. {{deal.budget|money}} */
export function placeholderFor(path: string, type: string): string {
  return `{{${path}${FILTER_HINT[type] ?? ""}}}`;
}

/**
 * Demo-Probelauf 29.10.2026: Platzhalter wie {{contract.number}} sind im Textfeld schwer lesbar. Unter dem Feld steht der
 * Abschnitt deshalb noch einmal in Klartext, jeder Platzhalter als markierter Name („Vertragsnummer“).
 */
export function lesbareTeile(text: string, customVars: readonly CustomVariableDef[]): { text: string; platzhalter?: boolean }[] {
  const teile: { text: string; platzhalter?: boolean }[] = [];
  let pos = 0;
  for (const m of text.matchAll(/\{\{\s*([^}]*?)\s*\}\}/g)) {
    if (m.index! > pos) teile.push({ text: text.slice(pos, m.index) });
    pos = m.index! + m[0].length;
    const roh = m[1]!.split("|")[0]!.trim();
    if (roh === "/if" || roh === "else") continue;
    const bedingung = roh.startsWith("#if ");
    const pfad = bedingung ? roh.slice(4).trim() : roh;
    const eigene = pfad.startsWith("var.") ? customVars.find((v) => v.key === pfad.slice(4)) : undefined;
    const sys = eigene ? undefined : systemVariable(pfad);
    const name = eigene ? nameIn(eigene.label.de, eigene.label.en) : sys ? nameIn(sys.label.de, sys.label.en) : pfad;
    teile.push(bedingung ? { text: t(`nur wenn ${name}:`, `only if ${name}:`), platzhalter: true } : { text: name, platzhalter: true });
  }
  if (pos < text.length) teile.push({ text: text.slice(pos) });
  return teile.map((x) => (x.platzhalter ? x : { text: x.text.replace(/\*\*|__|\*/g, "") }));
}

/**
 * Vertragstext in Abschnitten bearbeiten (keine HTML-Kenntnisse nötig). Platzhalter werden an der Schreibmarke des
 * zuletzt bearbeiteten Felds eingefügt. „Wenn …“ bis „Ende der Bedingung“ zeigt Text nur, wenn die Angabe zutrifft.
 * Neue Abschnitte gehen an jede Stelle („Hier einfügen“) oder vor die Unterschriften; Abschnitte lassen sich ziehen oder
 * hinter eine Überschrift verschieben; mehrzeiliger Text wird mit Überschriften und Listen übernommen (Befund 17).
 */
export function AbschnittEditor({ blocks, onChange, customVars, label, disabled }: {
  blocks: Block[];
  onChange: (b: Block[]) => void;
  customVars: readonly CustomVariableDef[];
  label: string;
  disabled?: boolean;
}) {
  const focus = useRef<{ id: string; el: HTMLTextAreaElement } | null>(null);
  const [add, setAdd] = useState<BlockKind>("p");
  const [ns, setNs] = useState<string>("var");
  const [paste, setPaste] = useState("");
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const depths = blockDepths(blocks);
  const problems = blockProblems(blocks);

  const update = (id: string, patch: Partial<Block>) => onChange(blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  };
  const remove = (i: number) => onChange(blocks.filter((_, k) => k !== i));
  /** Abschnitt an eine andere Stelle: `to` ist die Stelle vor dem Verschieben (der Abschnitt steht danach davor). */
  const moveTo = (from: number, to: number) => {
    if (from === to || from + 1 === to) return;
    const next = [...blocks];
    const [b] = next.splice(from, 1);
    next.splice(to > from ? to - 1 : to, 0, b!);
    onChange(next);
  };
  const headings = blocks.map((b, i) => ({ b, i })).filter((x) => x.b.kind === "h1" || x.b.kind === "h2" || x.b.kind === "h3");
  const insertAfter = (i: number, kind: BlockKind) => {
    const items: Block[] = kind === "if"
      ? [{ id: blockId(), kind: "if", text: "", cond: "" }, { id: blockId(), kind: "p", text: "" }, { id: blockId(), kind: "endif", text: "" }]
      : [{ id: blockId(), kind, text: "", ...(kind === "signature" ? { role: "talent" as ContractRole } : {}) }];
    const next = [...blocks];
    next.splice(i + 1, 0, ...items);
    onChange(next);
  };
  // K-544: Werkzeugleiste für das zuletzt bearbeitete Feld: fett, kursiv, unterstrichen (auch Strg/Cmd+B/I/U), Aufzählung,
  // Nummerierung, Platzhalter. Ausgezeichnet wird mit den Zeichen, die das Textformat schon kennt (**, *, __); keine Schriftwahl.
  const [aktiv, setAktiv] = useState<string | null>(null);
  const auszeichnen = (marke: "**" | "*" | "__") => {
    const f = focus.current;
    const target = f ? blocks.find((b) => b.id === f.id) : undefined;
    if (!f || !target || !TEXT_KINDS.includes(target.kind) || target.kind === "html") return;
    const start = f.el.selectionStart ?? target.text.length;
    const end = f.el.selectionEnd ?? start;
    const { text, a, e } = auszeichnungUmschalten(target.text, start, end, marke);
    update(target.id, { text });
    requestAnimationFrame(() => { f.el.focus(); f.el.setSelectionRange(a, e); });
  };
  /** Absatz ↔ Aufzählung bzw. Nummerierung (ein Punkt je Zeile) */
  const listeUmschalten = (art: "ul" | "ol") => {
    const f = focus.current;
    const target = f ? blocks.find((b) => b.id === f.id) : undefined;
    if (!target || !["p", "ul", "ol"].includes(target.kind)) return;
    update(target.id, { kind: target.kind === art ? "p" : art });
    requestAnimationFrame(() => f?.el.focus());
  };
  const tastenkuerzel = (e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    if (!(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey) return;
    const k = e.key.toLowerCase();
    const marke = k === "b" ? "**" : k === "i" ? "*" : k === "u" ? "__" : null;
    if (!marke) return;
    e.preventDefault();
    auszeichnen(marke);
  };
  const aktivBlock = aktiv ? blocks.find((b) => b.id === aktiv) : undefined;
  const formatierbar = !!aktivBlock && TEXT_KINDS.includes(aktivBlock.kind) && aktivBlock.kind !== "html";

  const insertPlaceholder = (token: string) => {
    const f = focus.current;
    const target = f ? blocks.find((b) => b.id === f.id) : undefined;
    if (!f || !target) {
      // ohne gewähltes Feld: als neuer Absatz am Ende
      onChange([...blocks, { id: blockId(), kind: "p", text: token }]);
      return;
    }
    const start = f.el.selectionStart ?? target.text.length;
    const end = f.el.selectionEnd ?? start;
    const text = target.text.slice(0, start) + token + target.text.slice(end);
    update(target.id, { text });
    requestAnimationFrame(() => {
      f.el.focus();
      f.el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const conditionOptions = [
    ...SYSTEM_VARIABLES.filter((v) => v.type === "boolean").map((v) => ({ value: v.path, label: nameIn(v.label.de, v.label.en) })),
    ...customVars.map((v) => ({ value: `var.${v.key}`, label: t(`${v.label.de} (eigene Angabe)`, `${nameIn(v.label.de, v.label.en)} (own field)`) })),
  ];
  const groups = [...new Set(SYSTEM_VARIABLES.map((v) => v.path.split(".")[0]!))];

  return (
    <section className="stack" aria-label={label}>
      {!disabled && (
        <div className="tk-format-leiste" role="toolbar" aria-label={t(`Format: ${label}`, `Formatting: ${label}`)}>
          {/* onMouseDown verhindert, dass das Textfeld den Fokus (und die Markierung) verliert */}
          <button type="button" className="tk-format-fett" disabled={!formatierbar} onMouseDown={(e) => e.preventDefault()} onClick={() => auszeichnen("**")} title={t("Fett (Strg/Cmd+B)", "Bold (Ctrl/Cmd+B)")} aria-label={t("Fett", "Bold")}>F</button>
          <button type="button" className="tk-format-kursiv" disabled={!formatierbar} onMouseDown={(e) => e.preventDefault()} onClick={() => auszeichnen("*")} title={t("Kursiv (Strg/Cmd+I)", "Italic (Ctrl/Cmd+I)")} aria-label={t("Kursiv", "Italic")}>K</button>
          <button type="button" className="tk-format-unter" disabled={!formatierbar} onMouseDown={(e) => e.preventDefault()} onClick={() => auszeichnen("__")} title={t("Unterstrichen (Strg/Cmd+U)", "Underlined (Ctrl/Cmd+U)")} aria-label={t("Unterstrichen", "Underlined")}>U</button>
          <span className="tk-format-trenner" aria-hidden="true" />
          <button type="button" disabled={!aktivBlock || !["p", "ul", "ol"].includes(aktivBlock.kind)} aria-pressed={aktivBlock?.kind === "ul"} onMouseDown={(e) => e.preventDefault()} onClick={() => listeUmschalten("ul")} title={t("Aufzählung: ein Punkt je Zeile", "Bulleted list: one item per line")} aria-label={t("Aufzählung", "Bullets")}>•</button>
          <button type="button" disabled={!aktivBlock || !["p", "ul", "ol"].includes(aktivBlock.kind)} aria-pressed={aktivBlock?.kind === "ol"} onMouseDown={(e) => e.preventDefault()} onClick={() => listeUmschalten("ol")} title={t("Nummerierung: ein Punkt je Zeile", "Numbered list: one item per line")} aria-label={t("Nummerierung", "Numbering")}>1.</button>
          <span className="tk-format-trenner" aria-hidden="true" />
          <label className="tk-format-platzhalter">
            <span className="sr-only">{t("Platzhalter einfügen", "Insert placeholder")}</span>
            <select value="" aria-label={t("Platzhalter einfügen", "Insert placeholder")} onChange={(e) => { if (e.target.value) insertPlaceholder(e.target.value); }}>
              <option value="">{t("Platzhalter einfügen …", "Insert placeholder …")}</option>
              {customVars.length > 0 && (
                <optgroup label={t("Eigene Angaben", "Own fields")}>
                  {customVars.map((v) => <option key={v.key} value={placeholderFor(`var.${v.key}`, v.type)}>{nameIn(v.label.de, v.label.en)}</option>)}
                </optgroup>
              )}
              {groups.map((g) => (
                <optgroup key={g} label={NS_LABEL[g] ?? g}>
                  {SYSTEM_VARIABLES.filter((v) => v.path.startsWith(`${g}.`)).map((v) => <option key={v.path} value={placeholderFor(v.path, v.type)}>{nameIn(v.label.de, v.label.en).replace(/^[^:]+:\s*/, "")}</option>)}
                </optgroup>
              ))}
            </select>
          </label>
          <small className="tk-format-hinweis">{aktivBlock ? t(`Wirkt auf: ${BLOCK_LABEL[aktivBlock.kind]}`, `Applies to: ${BLOCK_LABEL[aktivBlock.kind]}`) : t("Erst in einen Abschnitt klicken.", "Click into a section first.")}</small>
        </div>
      )}
      <div className="tk-blocks">
        {blocks.map((b, i) => {
          const cond = b.kind === "if" || b.kind === "else" || b.kind === "endif";
          return (
            <div key={b.id} className={`tk-block${cond ? " tk-block-cond" : ""}`} data-depth={Math.min(depths[i] ?? 0, 3)}
              onDragOver={(e) => { if (dragFrom !== null) e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); if (dragFrom !== null) moveTo(dragFrom, dragFrom < i ? i + 1 : i); setDragFrom(null); }}>
              <div className="stack">
                <span className="tk-block-kind">{BLOCK_LABEL[b.kind]}</span>
                {(b.kind === "h1" || b.kind === "h2" || b.kind === "h3" || b.kind === "p" || b.kind === "note" || b.kind === "ol" || b.kind === "ul" || b.kind === "html") && (
                  <textarea
                    aria-label={`${BLOCK_LABEL[b.kind]} ${i + 1}`}
                    className={b.kind.startsWith("h") ? "tk-block-h" : b.kind === "html" ? "tk-mono" : undefined}
                    value={b.text}
                    rows={b.kind === "ol" || b.kind === "ul" ? Math.max(3, b.text.split("\n").length + 1) : b.kind.startsWith("h") ? 1 : Math.max(2, Math.ceil(b.text.length / 90))}
                    placeholder={b.kind === "ol" || b.kind === "ul" ? t("Ein Punkt je Zeile", "One item per line") : b.kind === "html" ? "HTML" : t("Text, **fett**, *kursiv*, __unterstrichen__, Platzhalter", "Text, **bold**, *italic*, __underlined__, placeholders")}
                    onFocus={(e) => { focus.current = { id: b.id, el: e.currentTarget }; setAktiv(b.id); }}
                    onKeyDown={tastenkuerzel}
                    onChange={(e) => update(b.id, { text: e.target.value })}
                    disabled={disabled}
                  />
                )}
                {b.kind !== "html" && b.text.includes("{{") && (
                  <p className="tk-block-lesbar">
                    <span className="tk-block-lesbar-titel">{t("So steht es im Vertrag:", "How it reads in the contract:")}</span>{" "}
                    {lesbareTeile(b.text, customVars).map((x, k) => (x.platzhalter ? <span key={k} className="tk-ph">{x.text}</span> : <span key={k}>{x.text}</span>))}
                  </p>
                )}
                {(b.kind === "h1" || b.kind === "h2" || b.kind === "h3" || b.kind === "p") && (
                  // PARITAET-R3-8: Ausrichtung wie im bisherigen Editor
                  <label className="tk-block-align">
                    {t("Ausrichtung", "Alignment")}
                    <select value={b.align ?? ""} disabled={disabled} onChange={(e) => update(b.id, { align: (e.target.value || undefined) as BlockAlign | undefined })}>
                      <option value="">{t("Links", "Left")}</option>
                      <option value="center">{t("Mittig", "Centred")}</option>
                      <option value="right">{t("Rechts", "Right")}</option>
                    </select>
                  </label>
                )}
                {b.kind === "pagebreak" && <small>{t("Im PDF und beim Drucken beginnt hier eine neue Seite.", "In the PDF and when printing, a new page starts here.")}</small>}
                {b.kind === "signature" && (
                  <label>
                    {t("Unterschrift von", "Signature of")}<select value={b.role ?? "talent"} onChange={(e) => update(b.id, { role: e.target.value as ContractRole })} disabled={disabled}>
                      {(["agency", "brand", "talent"] as ContractRole[]).map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                    </select>
                  </label>
                )}
                {b.kind === "if" && (
                  <label>
                    {t("Nur wenn zutrifft", "Only if applicable")}<select value={b.cond ?? ""} onChange={(e) => update(b.id, { cond: e.target.value })} disabled={disabled}>
                      <option value="">{t("Angabe wählen", "Choose field")}</option>
                      {conditionOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      {b.cond && !conditionOptions.some((o) => o.value === b.cond) && <option value={b.cond}>{b.cond}</option>}
                    </select>
                  </label>
                )}
                {b.kind === "else" && <small>{t("Folgender Text gilt, wenn die Angabe nicht zutrifft.", "The following text applies if the field does not apply.")}</small>}
                {b.kind === "endif" && <small>{t("Ab hier gilt der Text wieder immer.", "From here the text always applies again.")}</small>}
              </div>
              {!disabled && (
                <div className="tk-block-tools">
                  <span className="tk-drag" draggable title={t("Ziehen zum Verschieben", "Drag to move")} aria-hidden="true" onDragStart={(e) => { setDragFrom(i); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(i)); }} onDragEnd={() => setDragFrom(null)}>⠿</span>
                  <button type="button" aria-label={t(`Abschnitt ${i + 1} nach oben`, `Section ${i + 1} up`)} onClick={() => move(i, -1)} disabled={i === 0}>↑</button>
                  <button type="button" aria-label={t(`Abschnitt ${i + 1} nach unten`, `Section ${i + 1} down`)} onClick={() => move(i, 1)} disabled={i === blocks.length - 1}>↓</button>
                  {headings.length > 1 && (
                    <select aria-label={t(`Abschnitt ${i + 1} verschieben hinter`, `Move section ${i + 1} after`)} value="" onChange={(e) => { if (e.target.value !== "") moveTo(i, Number(e.target.value) + 1); }}>
                      <option value="">{t("Verschieben …", "Move …")}</option>
                      {headings.filter((h) => h.i !== i).map((h) => <option key={h.b.id} value={h.i}>{h.b.text.slice(0, 40) || BLOCK_LABEL[h.b.kind]}</option>)}
                    </select>
                  )}
                  <button type="button" aria-label={t(`Nach Abschnitt ${i + 1} einfügen`, `Insert after section ${i + 1}`)} onClick={() => insertAfter(i, add)} title={t("Neuen Abschnitt hier einfügen", "Insert a new section here")}>{t("+ Einfügen", "+ Insert")}</button>
                  {b.kind === "if" && !blocks.slice(i + 1).some((x, k) => x.kind === "else" && (depths[i + 1 + k] ?? 0) === (depths[i] ?? 0)) && (
                    <button type="button" onClick={() => {
                      const end = blocks.findIndex((x, k) => k > i && x.kind === "endif" && (depths[k] ?? 0) === (depths[i] ?? 0));
                      const next = [...blocks];
                      next.splice(end < 0 ? blocks.length : end, 0, { id: blockId(), kind: "else", text: "" }, { id: blockId(), kind: "p", text: "" });
                      onChange(next);
                    }}>{t("Sonst ergänzen", "Otherwise add")}</button>
                  )}
                  <button type="button" aria-label={t(`Abschnitt ${i + 1} entfernen`, `Remove section ${i + 1}`)} onClick={() => remove(i)} title={t("Abschnitt entfernen", "Remove section")} className="tk-block-entfernen">✕</button>
                </div>
              )}
            </div>
          );
        })}
        {blocks.length === 0 && <p>{t("Noch kein Text. Füge unten den ersten Abschnitt hinzu.", "No text yet. Add the first section below.")}</p>}
      </div>
      {problems.length > 0 && <ul className="tk-error" role="alert">{problems.map((p) => <li key={p}>{p}</li>)}</ul>}
      {!disabled && (
        <div className="row">
          <label className="sr-only" htmlFor={`${label}-neu`}>{t("Art des neuen Abschnitts", "Type of the new section")}</label>
          <select id={`${label}-neu`} value={add} onChange={(e) => setAdd(e.target.value as BlockKind)}>
            {ADDABLE.map((k) => <option key={k} value={k}>{BLOCK_LABEL[k]}</option>)}
          </select>
          <button type="button" onClick={() => insertAfter(insertionIndex(blocks) - 1, add)}>{t("Abschnitt hinzufügen", "Add section")}</button>
          <small>{add === "signature" ? t("Unterschriftsfelder kommen ans Ende.", "Signature fields go at the end.") : t("Neue Abschnitte kommen vor die Unterschriften; „+ Einfügen“ an einem Abschnitt setzt sie direkt dahinter.", "New sections go before the signatures; “+ Insert” on a section places them directly after it.")}</small>
        </div>
      )}
      {!disabled && (
        <details className="panel">
          <summary>{t("Text einfügen (etwa aus Word oder PDF)", "Paste text (e.g. from Word or PDF)")}</summary>
          <p>{t("Überschriften wie „§ 3 Vergütung“, nummerierte Absätze („1.“, „(1)“) und Aufzählungen („-“, „•“) werden erkannt. Der Text kommt vor die Unterschriften.", "Headings such as “§ 3 Vergütung”, numbered paragraphs (“1.”, “(1)”) and lists (“-”, “•”) are recognised. The text goes before the signatures.")}</p>
          <label className="sr-only" htmlFor={`${label}-einfuegen`}>{t("Text zum Einfügen", "Text to paste")}</label>
          <textarea id={`${label}-einfuegen`} rows={6} value={paste} onChange={(e) => setPaste(e.target.value)} placeholder={"§ 1 Gegenstand\n(1) …\n(2) …"} />
          <div className="row">
            <button type="button" disabled={!paste.trim()} onClick={() => {
              const add = blocksFromText(paste);
              const at = insertionIndex(blocks);
              onChange([...blocks.slice(0, at), ...add, ...blocks.slice(at)]);
              setPaste("");
            }}>{t("Als Abschnitte übernehmen", "Take over as sections")}</button>
            {paste.trim() && <small>{blocksFromText(paste).length} {t("Abschnitte erkannt", "sections recognised")}</small>}
          </div>
        </details>
      )}
      {!disabled && (
        <details className="panel">
          <summary>{t("Platzhalter einfügen", "Insert placeholder")}</summary>
          <p>{t("Klick fügt den Platzhalter an der Schreibmarke des zuletzt bearbeiteten Abschnitts ein.", "A click inserts the placeholder at the cursor of the section last edited.")}</p>
          <div className="toolbar" role="group" aria-label={t("Bereich der Platzhalter", "Placeholder area")}>
            <button type="button" aria-pressed={ns === "var"} onClick={() => setNs("var")}>{t("Eigene Angaben", "Own fields")}</button>
            {groups.map((g) => <button key={g} type="button" aria-pressed={ns === g} onClick={() => setNs(g)}>{NS_LABEL[g] ?? g}</button>)}
            <button type="button" aria-pressed={ns === "signature"} onClick={() => setNs("signature")}>{t("Unterschrift", "Signature")}</button>
          </div>
          <div className="tk-chips">
            {ns === "var" && customVars.map((v) => (
              <button key={v.key} type="button" title={placeholderFor(`var.${v.key}`, v.type)} onClick={() => insertPlaceholder(placeholderFor(`var.${v.key}`, v.type))}>{nameIn(v.label.de, v.label.en)}</button>
            ))}
            {ns === "var" && customVars.length === 0 && <small>{t("Diese Vorlage hat noch keine eigenen Angaben.", "This template has no own fields yet.")}</small>}
            {ns === "signature" && (["agency", "brand", "talent"] as ContractRole[]).map((r) => (
              <button key={r} type="button" onClick={() => {
                // Unterschriftsfelder sind eigene Abschnitte (nie mitten im Absatz)
                const at = focus.current ? blocks.findIndex((b) => b.id === focus.current!.id) : blocks.length - 1;
                const next = [...blocks];
                next.splice(at + 1, 0, { id: blockId(), kind: "signature", text: "", role: r });
                onChange(next);
              }}>{ROLE_LABEL[r]}</button>
            ))}
            {ns !== "var" && ns !== "signature" && SYSTEM_VARIABLES.filter((v) => v.path.startsWith(`${ns}.`)).map((v) => (
              <button key={v.path} type="button" title={placeholderFor(v.path, v.type)} onClick={() => insertPlaceholder(placeholderFor(v.path, v.type))}>{nameIn(v.label.de, v.label.en).replace(/^[^:]+:\s*/, "")}</button>
            ))}
          </div>
        </details>
      )}
    </section>
  );
}
